import Header from "../../components/Header/Header";
import NavInf from "../../components/NavInf/NavInf";
import Footer from "../../components/Footer/Footer";
import Alert from "../../components/Alert/Alert";
import CreateProviderModal from "../../components/CreateProviderModal/CreateProviderModal";
import './MisProveedores.css';
import { useEffect, useState, useCallback } from "react";
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { secureFetch } from '../../utils/secureFetch';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faPlus, faEye, faTimes, faPhone, faDollarSign, faCalendarAlt, faUser, faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

const MisProveedores = () => {
    const [busqueda, setBusqueda] = useState('');
    const [proveedores, setProveedores] = useState([]);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [proveedorEditar, setProveedorEditar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedProveedor, setSelectedProveedor] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);

    const fetchProveedores = useCallback(async () => {
        try {
            const res = await secureFetch('https://api.minymol.com/debts/providers');
            if (!res.ok) throw new Error('Error cargando proveedores');
            const data = await res.json();
            
            // Filtrar por búsqueda si hay texto
            const filteredData = busqueda.trim() 
                ? data.filter(proveedor => 
                    proveedor.name.toLowerCase().includes(busqueda.toLowerCase()) ||
                    proveedor.phone.includes(busqueda)
                )
                : data;
            
            setProveedores(filteredData);
            setLoading(false);
        } catch (err) {
            console.error('Error al cargar proveedores:', err);
            setAlertType('error');
            setAlertMessage('Error al cargar proveedores');
            setShowAlert(true);
            setLoading(false);
        }
    }, [busqueda]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                fetchProveedores();
            }
        });

        return () => unsubscribe();
    }, [fetchProveedores]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.provider-card-menu')) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleEliminar = async (proveedorId) => {
        try {
            const res = await secureFetch(`https://api.minymol.com/debts/providers/${proveedorId}`, {
                method: 'DELETE'
            });
            if (!res.ok) throw new Error('Error al eliminar proveedor');
            
            setProveedores(prev => prev.filter(p => p.id !== proveedorId));
            setAlertType('success');
            setAlertMessage('Proveedor eliminado correctamente');
            setShowAlert(true);
        } catch (err) {
            setAlertType('error');
            setAlertMessage('Error al eliminar proveedor');
            setShowAlert(true);
        }
    };

    const handleCreateProvider = async ({ name, phone }) => {
        try {
            const res = await secureFetch('https://api.minymol.com/debts/providers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, phone }),
            });
            if (!res.ok) throw new Error('Error al crear proveedor');
            
            const nuevoProveedor = await res.json();
            setProveedores(prev => [...prev, nuevoProveedor]);
            setAlertType('success');
            setAlertMessage('Proveedor creado correctamente');
            setShowAlert(true);
            setIsModalOpen(false);
        } catch (err) {
            setAlertType('error');
            setAlertMessage('Error al crear proveedor');
            setShowAlert(true);
        }
    };

    const handleUpdateProvider = async ({ name, phone }) => {
        try {
            const res = await secureFetch(`https://api.minymol.com/debts/providers/${proveedorEditar.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, phone }),
            });
            if (!res.ok) throw new Error('Error al actualizar proveedor');
            
            const proveedorActualizado = await res.json();
            setProveedores(prev => 
                prev.map(p => p.id === proveedorEditar.id ? proveedorActualizado : p)
            );
            setAlertType('success');
            setAlertMessage('Proveedor actualizado correctamente');
            setShowAlert(true);
            setProveedorEditar(null);
            setIsModalOpen(false);
        } catch (err) {
            setAlertType('error');
            setAlertMessage('Error al actualizar proveedor');
            setShowAlert(true);
        }
    };

    const handleEditar = (proveedor) => {
        setProveedorEditar(proveedor);
        setIsModalOpen(true);
    };

    const handleViewDetails = (proveedor) => {
        setSelectedProveedor(proveedor);
        setShowDetailsModal(true);
    };

    const formatPhone = (phone) => {
        if (!phone) return '';
        return phone.replace(/^\+57/, '');
    };

    const handleCardClick = (proveedor) => {
        window.location.href = `/movimientos?providerId=${proveedor.id}`;
    };

    const toggleDropdown = (proveedorId, e) => {
        e.stopPropagation();
        setOpenDropdown(openDropdown === proveedorId ? null : proveedorId);
    };

    const handleOpenWhatsApp = (phone, e) => {
        e.stopPropagation();
        const clean = (phone || "").replace(/\D/g, "");
        const normalized = clean.startsWith("57") ? clean : `57${clean}`;
        window.open(`https://wa.me/${normalized}`, '_blank');
    };

    if (loading) {
        return (
            <>
                <Header
                    minimal={true}
                    providerName={JSON.parse(localStorage.getItem('usuario'))?.proveedorInfo?.nombre_empresa}
                    menuProvider={true}
                    currentPage='proveedores'
                />
                <div className="providers-container">
                    <div className="loading">Cargando proveedores...</div>
                </div>
                <NavInf />
                <Footer navinf={true} />
            </>
        );
    }

    return (
        <>
            {showAlert && (
                <Alert
                    type={alertType}
                    message={alertMessage}
                    onClose={() => setShowAlert(false)}
                />
            )}

            <Header />
        
            <div className="providers-container">
                <div className="providers-header">
                    <div className="busqueda-proveedores">
                    <input
                        type="text"
                        placeholder="Buscar por nombre o teléfono"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') fetchProveedores();
                        }}
                    />
                    <button onClick={fetchProveedores}>Buscar</button>
                    </div>
                    
                    <button 
                        className="add-provider-btn"
                        onClick={() => {
                            setProveedorEditar(null);
                            setIsModalOpen(true);
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Nuevo
                    </button>
                </div>

                <div className="providers-grid">
                    {proveedores.map((proveedor) => (
                        <div 
                            key={proveedor.id} 
                            className="provider-card"
                            onClick={() => handleCardClick(proveedor)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="provider-card-header">
                                <div className="provider-name">
                                    <h3>{proveedor.name}</h3>
                                </div>
                                
                                <div className="provider-card-menu">
                                    <button
                                        className="btn-dropdown"
                                        onClick={(e) => toggleDropdown(proveedor.id, e)}
                                        title="Opciones"
                                    >
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                    </button>
                                    
                                    {openDropdown === proveedor.id && (
                                        <div className="dropdown-menu">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenDropdown(null);
                                                    handleViewDetails(proveedor);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                                Ver detalle
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenDropdown(null);
                                                    handleEditar(proveedor);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPen} />
                                                Editar
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenDropdown(null);
                                                    handleEliminar(proveedor.id);
                                                }}
                                                className="delete-option"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                                Eliminar
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="provider-card-footer">
                                <div className="provider-debt">
                                    <span className="debt-amount">
                                        ${proveedor?.deudaRestante != null
                                            ? proveedor.deudaRestante.toLocaleString('es-CO')
                                            : '0'}
                                    </span>
                                </div>
                                
                                <button
                                    className="btn-whatsapp"
                                    onClick={(e) => handleOpenWhatsApp(proveedor.phone, e)}
                                    title="Enviar WhatsApp"
                                >
                                    <FontAwesomeIcon icon={faWhatsapp} />
                                </button>
                            </div>
                        </div>
                    ))}
                    
                    {proveedores.length === 0 && (
                        <div className="no-data">
                            {busqueda.trim() ? 'No se encontraron proveedores con esa búsqueda' : 'No tienes proveedores registrados'}
                        </div>
                    )}
                </div>
            </div>

            <CreateProviderModal
                visible={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setProveedorEditar(null);
                }}
                onCreate={proveedorEditar ? handleUpdateProvider : handleCreateProvider}
                editData={proveedorEditar}
            />

            {showDetailsModal && selectedProveedor && (
                <div className="details-modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="details-modal-header">
                            <h2>
                                <FontAwesomeIcon icon={faUser} className="header-icon" />
                                {selectedProveedor.name}
                            </h2>
                            <button 
                                className="close-modal-btn"
                                onClick={() => setShowDetailsModal(false)}
                                title="Cerrar"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="details-modal-body">
                            <div className="detail-row">
                                <span className="detail-label">
                                    <FontAwesomeIcon icon={faPhone} className="detail-icon" />
                                    Teléfono
                                </span>
                                <span className="detail-value">{formatPhone(selectedProveedor.phone)}</span>
                            </div>
                            
                            <div className="detail-row">
                                <span className="detail-label">
                                    <FontAwesomeIcon icon={faDollarSign} className="detail-icon" />
                                    Deuda Actual
                                </span>
                                <span className="detail-value debt-amount">
                                    ${selectedProveedor?.deudaRestante != null
                                        ? selectedProveedor.deudaRestante.toLocaleString('es-CO')
                                        : '0'}
                                </span>
                            </div>
                            
                            <div className="detail-row">
                                <span className="detail-label">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="detail-icon" />
                                    Último Movimiento
                                </span>
                                <span className="detail-value">
                                    {selectedProveedor?.lastMovement || 'Sin movimientos'}
                                    {selectedProveedor?.lastType && (
                                        <span className="movement-type"> ({selectedProveedor.lastType})</span>
                                    )}
                                </span>
                            </div>
                        </div>
                        
                        <div className="details-modal-actions">
                            <button
                                className="modal-action-btn edit"
                                onClick={() => {
                                    setShowDetailsModal(false);
                                    handleEditar(selectedProveedor);
                                }}
                            >
                                <FontAwesomeIcon icon={faPen} />
                                Editar
                            </button>
                            <button
                                className="modal-action-btn delete"
                                onClick={() => {
                                    setShowDetailsModal(false);
                                    handleEliminar(selectedProveedor.id);
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <NavInf />
            <Footer navinf={true} />
        </>
    );
};

export default MisProveedores;
